import React, { FC, useCallback, useContext, useState } from 'react';
import { IMfaFullDevice } from '../../resources/api/apiUser';
import { useTranslation } from 'react-i18next';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { useEnumState } from '../../hooks/useEnumState';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { TypographyBody1 } from '../Typography/Typography';
import { CodeInput } from '../CodeInput/CodeInput';
import { HiddenSubmit } from '../HiddenSubmit/HiddenSubmit';
import { DangerousButton } from '../CustomButtons/DangerousButton';
import { apiAuth } from '../../resources/api/apiAuth';
import { NotificationContext } from '../../contexts/NotificationContext';

const { useInitiateDisable2fa, useConfirmDisable2fa } = apiAuth;

enum EDisableState {
  Closed,
  Confirm,
  VerifyCode,
}

export const DisableDeviceButton: FC<{ device: IMfaFullDevice; onRefresh: () => void }> = ({
  device,
  onRefresh,
  children,
}) => {
  const { t } = useTranslation();
  const handleError = useApiErrorHandler();
  const { addNotification } = useContext(NotificationContext);

  const { execute: initiateDisable, Guard } = useInitiateDisable2fa();
  const { execute: confirmDisable } = useConfirmDisable2fa();

  const [disableState, openConfirm, setVerifyCode, closeDialog] = useEnumState(
    EDisableState.Closed,
    EDisableState.Confirm,
    EDisableState.VerifyCode,
    EDisableState.Closed
  );
  const [code, setCode] = useState('');
  const [apiMessage, setApiMessage] = useState('');

  const onInitiateDisable = useCallback(async () => {
    try {
      const result = await initiateDisable(device.device_id);
      setApiMessage(result.payload.message);
      setVerifyCode();
    } catch (e) {
      handleError(e);
    }
  }, [initiateDisable, device.device_id, setVerifyCode, handleError]);

  const onConfirmDisable = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();
      try {
        await confirmDisable(device.device_id, code);
        await onRefresh();
        addNotification({
          severity: 'success',
          message: t('security.mfa.disable.successMessage'),
        });
        setCode('');
        setApiMessage('');
        closeDialog();
      } catch (e) {
        handleError(e);
      }
    },
    [confirmDisable, device.device_id, code, onRefresh, addNotification, t, closeDialog, handleError]
  );

  const handleClose = useCallback(() => {
    setCode('');
    setApiMessage('');
    closeDialog();
  }, [closeDialog]);

  return (
    <Guard>
      <Button variant={'text'} color={'error'} onClick={openConfirm}>
        {children}
      </Button>

      <Dialog open={disableState !== EDisableState.Closed} onClose={handleClose}>
        <DialogTitle>{t('security.mfa.disable.title')}</DialogTitle>

        {disableState === EDisableState.Confirm ? (
          <DialogContent>
            <TypographyBody1>{t('security.mfa.disable.message')}</TypographyBody1>
          </DialogContent>
        ) : null}

        {disableState === EDisableState.VerifyCode ? (
          <DialogContent>
            <TypographyBody1>{apiMessage}</TypographyBody1>
            <form onSubmit={onConfirmDisable}>
              <CodeInput value={code} onChange={setCode} pattern={'ddd-ddd'} />
              <HiddenSubmit />
            </form>
          </DialogContent>
        ) : null}

        <DialogActions>
          <Button variant={'outlined'} onClick={handleClose}>
            {t('common.dialog.close')}
          </Button>

          {disableState === EDisableState.Confirm ? (
            <DangerousButton onClick={onInitiateDisable}>{t('security.mfa.disable.confirm')}</DangerousButton>
          ) : null}

          {disableState === EDisableState.VerifyCode ? (
            <DangerousButton onClick={() => onConfirmDisable()} disabled={code.length < 6}>
              {t('security.mfa.disable.submit')}
            </DangerousButton>
          ) : null}
        </DialogActions>
      </Dialog>
    </Guard>
  );
};
